// This show's Themes: the library, what the show wears, and one theme.
//
// PORTED FROM THE OCTOBER HAND-OFF'S console-themes.jsx (ThemesLibrary and
// ThemeDetail), STRUCTURALLY. The prototype kept the library in localStorage;
// here every read and every write is the service's (#199, admin/themes.ts),
// and the screens draw what it answers. Three things the prototype could fake
// and this cannot:
//
//   NOVA HOUSE IS LISTED FIRST AND IS NOT A ROW. It ships in code, it is read
//   only, and nothing can point at it: the service answers 409
//   `house_theme_is_not_a_row`. So it is on the cards, it opens read-only with
//   "Copy to edit", and it is in no select. A select offering it would be an
//   option that always refuses.
//
//   COPY TO EDIT (Gabe, 12 Oct, decision C8). The house theme stays read
//   only; its page offers the prototype's "Copy to edit", which makes an
//   editable copy for this show's client (or the client whose page it was
//   opened from) through the ordinary create route, and opens it. An Editor
//   may do it for a client they hold: the service scopes the create by the
//   client named (admin/routes.ts, requireThemeSession) and its refusal is
//   drawn here as it is sent.
//
//   "FOLLOWS THE SHOW" AND "ITS OWN SYSTEM" ARE TWO ANSWERS. The prototype
//   collapsed them into an empty select. On the wire a missing key follows the
//   show's event theme (and through it the client's default), and "own" is the
//   app on its own design system whatever the show wears. Each app's select
//   has an option for each, and themes/model.ts is where the two are written.
//
//   A DELETE IS AN ARCHIVE AND IT CLEARS EVERY POINTER AT ONCE. Not only this
//   show's: a client default naming the theme becomes no default, a show
//   wearing it follows its client, and an app pointed at it follows its show.
//   The row stays as the record of what a published bundle wore. The confirm
//   says so, and asks for the word typed when an app here is wearing it.
//
// NOTHING HERE PUBLISHES. Pointing an app at a theme, or saving a theme, is the
// draft a stand will wear the next time that app is published; a stand
// already published keeps its colours until then. The pages say it where it
// matters, which is the confirm and the (i)s, rather than on every control.
//
// NOVA-ONLY FOR NOW. Every theme route takes a nova_admin session (the roles
// change brings viewers), so the rail offers this page to Nova staff only and
// a deep link gets the service's own refusal, drawn by AdminRefused.

import { useCallback, useEffect, useState, type Dispatch, type ReactNode, type SetStateAction } from "react";
import { Crumbs, NoticeBox, PageHead, Setting, StickyBar, Switch } from "@nova/desk-ui/parts";
import { FieldRow, SecLabel } from "@nova/desk-ui/kit";
import { Confirm, Loading } from "@nova/desk-ui/states";
import { HOUSE_THEME, themeSaveProblems, type ThemeRole } from "../../../ui/src/themeLibrary.js";
import type { ApiOptions } from "../api/client.js";
import type { AdminRefusal } from "../api/admin.js";
import {
  archiveLibraryTheme,
  createLibraryTheme,
  listLibrary,
  readLibraryTheme,
  readShowThemes,
  sameId,
  saveShowThemes,
  updateLibraryTheme,
  type BelowFloor,
  type LibraryRecord,
  type ShowThemes,
  type ThemeDraft,
} from "../api/themes.js";
import type { SessionToken } from "../session/store.js";
import {
  ContrastTable,
  failingPairs,
  ModeSwitch,
  PaletteEditor,
  ShippedTag,
  ThemePreviews,
  ThemeSwatches,
  TypeAndCorners,
  WEARABLE_SURFACES,
  type ThemeMode,
} from "../themes/parts.js";
import {
  blankTheme,
  copyOf,
  draftOf,
  editedOn,
  flipWear,
  FOLLOW,
  followedThemeId,
  listed,
  nameOf,
  optionValue,
  OWN_SYSTEM,
  pointable,
  pointerOf,
  wears,
  wearersOf,
  withPointer,
} from "../themes/model.js";
import { AdminRefused } from "./Authorise.js";
import { useConsoleToast } from "./Chrome.js";
import { useShow } from "./Show.js";

export type ThemeRefusal = AdminRefusal | BelowFloor | { readonly kind: "local"; readonly detail: string };

/** A refusal from the service, the contrast floor's own, or this page's own before anything was sent. */
export function ThemeRefused({ refusal }: { readonly refusal: ThemeRefusal }): ReactNode {
  if (refusal.kind === "below_floor") {
    return (
      <NoticeBox kind="bad" alert title="The service refused this theme">
        <p>{refusal.detail}</p>
        <p className="quiet">Nothing was saved. Every surface wearing it still reads the last saved colours.</p>
      </NoticeBox>
    );
  }
  if (refusal.kind === "local") {
    return (
      <NoticeBox kind="bad" alert title="Not saved">
        <p>{refusal.detail}</p>
        <p className="quiet">Nothing was sent.</p>
      </NoticeBox>
    );
  }
  return <AdminRefused refusal={refusal} />;
}

export type ThemesProps = {
  readonly api: ApiOptions;
  readonly token: SessionToken;
  readonly activationId: string;
  readonly themeId: string | null;
  readonly onOpen: (themeId: string | null) => void;
  readonly onSessionEnded: (detail: string) => void;
};

type Read =
  | { readonly status: "reading" }
  | { readonly status: "failed"; readonly refusal: ThemeRefusal }
  | { readonly status: "ready"; readonly show: ShowThemes; readonly themes: readonly LibraryRecord[] };

const LIBRARY_LEDE =
  "A theme is a client's palette — twelve colours, a typeface pair and a corner — saved once and worn by " +
  "whichever surfaces you point at it. Each surface maps the same roles into its own tokens, so " +
  "Registration and the Wheel cannot drift apart.";

/**
 * The show's themes and its client's library, read in that order: the show's
 * read names the client, and the library is what that client may wear.
 */
function useShowLibrary(
  api: ApiOptions,
  token: SessionToken,
  activationId: string,
  onSessionEnded: (detail: string) => void,
): [Read, Dispatch<SetStateAction<Read>>, () => void] {
  const [read, setRead] = useState<Read>({ status: "reading" });
  const [generation, setGeneration] = useState(0);
  // After the frame's own read of the directory, the way Wheel waits for it,
  // so the show's name is on the page before its themes are asked for and the
  // requests go out in one order rather than whichever effect ran first. A
  // directory that failed does not hold this page up: the themes are their
  // own read and the service answers them on their own.
  const frame = useShow();
  const settled = frame.status === "ready" || frame.status === "failed";

  useEffect(() => {
    if (!settled) return undefined;
    let cancelled = false;
    void (async () => {
      const show = await readShowThemes(api, token, activationId);
      if (cancelled) return;
      if (!show.ok) {
        if (show.kind === "session_ended") onSessionEnded(show.detail);
        setRead({ status: "failed", refusal: show });
        return;
      }
      const themes = await listLibrary(api, token, show.value.clientId);
      if (cancelled) return;
      if (!themes.ok) {
        if (themes.kind === "session_ended") onSessionEnded(themes.detail);
        setRead({ status: "failed", refusal: themes });
        return;
      }
      setRead({ status: "ready", show: show.value, themes: themes.value });
    })();
    return () => {
      cancelled = true;
    };
  }, [api, token, activationId, onSessionEnded, generation, settled]);

  return [read, setRead, useCallback(() => setGeneration((n) => n + 1), [])];
}

export function Themes(props: ThemesProps): ReactNode {
  const [read, setRead, reload] = useShowLibrary(props.api, props.token, props.activationId, props.onSessionEnded);

  if (props.themeId !== null) {
    return (
      <ThemeDetail
        key={props.themeId}
        api={props.api}
        token={props.token}
        themeId={props.themeId}
        read={read}
        onShow={(show) => {
          setRead((now) => (now.status === "ready" ? { ...now, show } : now));
        }}
        onLibrary={(change) => {
          // An archive clears pointers this page cannot see, so the list is
          // read again, and until it is, nothing stale is drawn as live: the
          // archived theme is not left on a card or in a select (Bugbot on #203).
          if (change.kind === "reload" || read.status !== "ready") {
            setRead({ status: "reading" });
            reload();
          }
          else setRead((now) => (now.status === "ready" ? { ...now, themes: putTheme(now.themes, change.theme) } : now));
        }}
        onBack={() => props.onOpen(null)}
        onOpen={(id) => props.onOpen(id)}
        onSessionEnded={props.onSessionEnded}
        crumb="Themes"
      />
    );
  }

  return (
    <div className="stack">
      <PageHead title="Themes" lede={LIBRARY_LEDE} explain="info" />
      {read.status === "reading" ? <Loading rows={4} cols={3} label="Loading themes…" /> : null}
      {read.status === "failed" ? <ThemeRefused refusal={read.refusal} /> : null}
      {read.status === "ready" ? (
        <Library
          {...props}
          show={read.show}
          themes={read.themes}
          onShow={(show) => setRead((now) => (now.status === "ready" ? { ...now, show } : now))}
          // Merged by id into the list as it stands, so two quick additions both stay.
          onThemes={(themes) => setRead((now) => (now.status === "ready" ? { ...now, themes: themes.reduce(putTheme, now.themes) } : now))}
        />
      ) : null}
    </div>
  );
}

function Library({
  api,
  token,
  activationId,
  show,
  themes,
  onShow,
  onThemes,
  onOpen,
  onSessionEnded,
}: ThemesProps & {
  readonly show: ShowThemes;
  readonly themes: readonly LibraryRecord[];
  readonly onShow: (show: ShowThemes) => void;
  readonly onThemes: (themes: readonly LibraryRecord[]) => void;
}): ReactNode {
  const say = useConsoleToast();
  const [busy, setBusy] = useState(false);
  const [refusal, setRefusal] = useState<ThemeRefusal | null>(null);
  const clientName = useShowClientName();
  const choices = pointable(themes);
  const clientDefault = nameOf(themes, show.clientDefaultThemeId);
  const followed = nameOf(themes, followedThemeId(show));

  const write = async (
    showThemeId: string | null,
    surfaceThemes: ShowThemes["surfaceThemes"],
    receipt: (next: ShowThemes) => string,
  ): Promise<void> => {
    setBusy(true);
    setRefusal(null);
    const saved = await saveShowThemes(api, token, { activationId, showThemeId, surfaceThemes });
    setBusy(false);
    if (!saved.ok) {
      if (saved.kind === "session_ended") onSessionEnded(saved.detail);
      setRefusal(saved);
      return;
    }
    onShow(saved.value);
    say(receipt(saved.value));
  };

  const duplicate = async (theme: LibraryRecord): Promise<void> => {
    setBusy(true);
    setRefusal(null);
    // A copy belongs to this show's client, including a copy of Nova house:
    // that is the way a client comes to wear it.
    const made = await createLibraryTheme(api, token, { ...copyOf(theme), clientId: show.clientId });
    setBusy(false);
    if (!made.ok) {
      if (made.kind === "session_ended") onSessionEnded(made.detail);
      setRefusal(made);
      return;
    }
    onThemes([...themes, made.value]);
    say(`Copied. ${made.value.name} is a new theme and is not worn by anything yet.`);
  };

  const fresh = async (): Promise<void> => {
    setBusy(true);
    setRefusal(null);
    const made = await createLibraryTheme(api, token, { ...blankTheme("Untitled theme"), clientId: show.clientId });
    setBusy(false);
    if (!made.ok) {
      if (made.kind === "session_ended") onSessionEnded(made.detail);
      setRefusal(made);
      return;
    }
    // Into the library first: the theme page reads it from this list, and one
    // missing from it is drawn as archived, read-only (Bugbot on #203).
    onThemes([...themes, made.value]);
    onOpen(made.value.themeId);
  };

  return (
    <>
      {refusal === null ? null : <ThemeRefused refusal={refusal} />}

      <section>
        <SecLabel>Event theme</SecLabel>
        <div className="panel panel-stack">
          <Setting
            label="Every app wears"
            info="Registration, Wheel, Board, Demo and Kiosk follow it, unless an app is set to something else below or on its own Theme page."
          >
            <select
              className="input"
              name="event-theme"
              value={show.showThemeId === null ? "" : optionValue(themes, show.showThemeId)}
              disabled={busy}
              onChange={(event) => {
                const value = event.target.value === "" ? null : event.target.value;
                void write(value, show.surfaceThemes, () =>
                  value === null
                    ? clientDefault === null
                      ? "No event theme. Each app wears its own system."
                      : `No event theme. The show follows ${clientName ?? "the client"}'s default, ${clientDefault}.`
                    : `${nameOf(themes, value) ?? "That theme"} is the event theme.`,
                );
              }}
            >
              <option value="">
                {clientDefault === null
                  ? "None · each app on its own system"
                  : `The client’s default · ${clientDefault}`}
              </option>
              {choices.map((t) => (
                <option key={t.themeId} value={t.themeId}>
                  {t.name}
                </option>
              ))}
            </select>
          </Setting>
        </div>
      </section>

      <section>
        <SecLabel>Saved themes</SecLabel>
        <div className="tcards">
          {themes.map((t) => {
            const worn = wearersOf(show, t.themeId);
            const bad = failingPairs(t.light).length + failingPairs(t.dark).length;
            const edited = editedOn(t.revisedAt);
            const owner = t.clientId === null ? "Nova · any client" : (clientName ?? "This client");
            return (
              <article className="tcard" key={t.themeId}>
                <div className="tcard-h">
                  <div>
                    <h3>{t.name}</h3>
                    <p className="quiet">
                      {t.locked ? "Ships with the product" : edited === null ? owner : `${owner} · edited ${edited}`}
                    </p>
                  </div>
                  {t.locked ? <ShippedTag /> : null}
                </div>
                <div className="tcard-sw">
                  <ThemeSwatches p={t.light} />
                  <ThemeSwatches p={t.dark} />
                </div>
                <p className="tcard-w">{worn.length > 0 ? `Worn by ${worn.join(", ")}` : "Not worn by any surface"}</p>
                {bad > 0 ? (
                  <p className="warn tcard-bad">
                    {bad} pair{bad === 1 ? "" : "s"} below the floor. It cannot be published as it stands.
                  </p>
                ) : null}
                <div className="acts acts-flush">
                  <button type="button" className="btn btn-quiet btn-sm" onClick={() => onOpen(t.themeId)}>
                    {t.locked ? "Open" : "Edit"}
                  </button>
                  <button
                    type="button"
                    className="btn btn-quiet btn-sm"
                    disabled={busy}
                    onClick={() => void duplicate(t)}
                  >
                    Duplicate
                  </button>
                </div>
              </article>
            );
          })}
        </div>
        <div className="acts">
          <button type="button" className="btn btn-primary" disabled={busy} onClick={() => void fresh()}>
            New theme
          </button>
        </div>
      </section>

      <section>
        <SecLabel info="Pointing a surface at a theme changes the draft. The stand changes when that surface is published, not when this select does.">
          Which surface wears what
        </SecLabel>
        <div className="panel panel-stack">
          {WEARABLE_SURFACES.map(([id, label, , note]) => (
            <Setting key={id} label={label} info={note}>
              <select
                className="input"
                name={`surface-theme-${id}`}
                value={optionValue(themes, pointerOf(show, id))}
                disabled={busy}
                onChange={(event) => {
                  const value = event.target.value;
                  void write(show.showThemeId, withPointer(show, id, value), (next) =>
                    value === FOLLOW
                      ? `${label} follows the show again${
                          nameOf(themes, followedThemeId(next)) === null
                            ? ", which is on its own system."
                            : `, which wears ${nameOf(themes, followedThemeId(next))}.`
                        }`
                      : value === OWN_SYSTEM
                        ? `${label} is on its own design system.`
                        : `${label} now wears ${nameOf(themes, value) ?? "that theme"}.`,
                  );
                }}
              >
                <option value={FOLLOW}>
                  {followed === null ? "Follows the show · its own system" : `Follows the show · ${followed}`}
                </option>
                <option value={OWN_SYSTEM}>Its own design system</option>
                {choices.map((t) => (
                  <option key={t.themeId} value={t.themeId}>
                    {t.name}
                  </option>
                ))}
              </select>
            </Setting>
          ))}
        </div>
      </section>
    </>
  );
}

function useShowClientName(): string | null {
  const show = useShow();
  return show.status === "ready" && show.line !== null ? show.line.client.name : null;
}

// ------------------------------------------------------------- one theme

export type LibraryChange = { readonly kind: "put"; readonly theme: LibraryRecord } | { readonly kind: "reload" };

/** The list with one theme replaced where it was, or added at the end. */
export function putTheme(themes: readonly LibraryRecord[], theme: LibraryRecord): readonly LibraryRecord[] {
  return themes.some((t) => sameId(t.themeId, theme.themeId))
    ? themes.map((t) => (sameId(t.themeId, theme.themeId) ? theme : t))
    : [...themes, theme];
}

export type ThemeDetailProps = {
  readonly api: ApiOptions;
  readonly token: SessionToken;
  readonly themeId: string;
  /**
   * The show and the library this theme is read from, or the library alone
   * where it is opened from a client's page. Without a show there is no app to
   * switch on, and "Where it is worn" says so rather than drawing switches.
   */
  readonly read:
    | Read
    | { readonly status: "ready"; readonly show: null; readonly themes: readonly LibraryRecord[] };
  readonly onShow: (show: ShowThemes) => void;
  /**
   * After a write, so the library behind this page agrees with it. A saved or
   * copied theme is put into the list it came back as; an archive reads the
   * whole thing again, because it cleared pointers this page cannot see.
   */
  readonly onLibrary: (change: LibraryChange) => void;
  readonly onBack: () => void;
  readonly onOpen: (themeId: string) => void;
  readonly onSessionEnded: (detail: string) => void;
  /** The ancestor's word in the crumbs. */
  readonly crumb: string;
  /** Whose palette a new copy belongs to, where there is no show to say. */
  readonly clientId?: string | null;
  /**
   * Who wears this theme, where there is no show to ask: a client's page
   * names its default and each of its shows that wears it, so a delete from
   * there asks for the typed DELETE when it should (Bugbot on #203).
   */
  readonly wornElsewhere?: readonly string[];
  readonly clientName?: string | null;
};

export function ThemeDetail(props: ThemeDetailProps): ReactNode {
  const { read } = props;
  // A theme that is not in the library list may still be a row: archived, or
  // another client's. Read on its own, and drawn read-only if it is archived.
  const [single, setSingle] = useState<
    { readonly status: "idle" } | { readonly status: "read"; readonly theme: LibraryRecord } | { readonly status: "failed"; readonly refusal: ThemeRefusal }
  >({ status: "idle" });
  const listedTheme =
    read.status === "ready" ? (read.themes.find((t) => sameId(t.themeId, props.themeId)) ?? null) : null;
  const needSingle = read.status === "ready" && listedTheme === null;

  useEffect(() => {
    if (!needSingle) return undefined;
    let cancelled = false;
    void (async () => {
      const got = await readLibraryTheme(props.api, props.token, props.themeId);
      if (cancelled) return;
      if (!got.ok) {
        if (got.kind === "session_ended") props.onSessionEnded(got.detail);
        setSingle({ status: "failed", refusal: got });
        return;
      }
      setSingle({ status: "read", theme: got.value });
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- read once per theme id, when the list did not have it
  }, [needSingle, props.themeId]);

  const crumbs = <Crumbs items={[[props.crumb, props.onBack], ["Theme", null]]} />;
  if (read.status === "reading" || (needSingle && single.status === "idle")) {
    return (
      <div className="stack">
        {crumbs}
        <Loading rows={4} cols={2} label="Loading the theme…" />
      </div>
    );
  }
  if (read.status === "failed" || single.status === "failed") {
    const refusal = read.status === "failed" ? read.refusal : single.status === "failed" ? single.refusal : null;
    return (
      <div className="stack">
        {crumbs}
        <PageHead title="Theme" />
        {refusal === null ? null : <ThemeRefused refusal={refusal} />}
      </div>
    );
  }
  const theme = listedTheme ?? (single.status === "read" ? single.theme : null);
  // Archived is what the record says, not whether the library list happens to
  // carry the theme yet: a theme just made, by any of the New theme buttons,
  // can open before the list it was put in has rendered (Bugbot on #203).
  if (theme === null || read.status !== "ready") return crumbs;
  return <Editor {...props} theme={theme} show={read.show} themes={read.themes} crumbs={crumbs} archived={theme.archivedAt != null} />;
}

function Editor({
  api,
  token,
  theme,
  show,
  themes,
  crumbs,
  archived,
  onShow,
  onLibrary,
  onBack,
  onOpen,
  onSessionEnded,
  clientId,
  clientName,
  wornElsewhere,
}: ThemeDetailProps & {
  readonly theme: LibraryRecord;
  readonly show: ShowThemes | null;
  readonly themes: readonly LibraryRecord[];
  readonly crumbs: ReactNode;
  readonly archived: boolean;
}): ReactNode {
  const say = useConsoleToast();
  // On a client's page the caller names the client; inside a show, the show does.
  const showClient = useShowClientName();
  const owner = clientName ?? showClient;
  const [saved, setSaved] = useState<ThemeDraft>(() => draftOf(theme));
  const [draft, setDraft] = useState<ThemeDraft>(() => draftOf(theme));
  const [mode, setMode] = useState<ThemeMode>("light");
  const [ask, setAsk] = useState(false);
  const [busy, setBusy] = useState(false);
  const [refusal, setRefusal] = useState<ThemeRefusal | null>(null);
  const locked = theme.locked || theme.themeId === HOUSE_THEME.id || archived;
  const dirty = JSON.stringify(draft) !== JSON.stringify(saved);
  const worn = show === null ? (wornElsewhere ?? []) : wearersOf(show, theme.themeId);
  const bad = failingPairs(draft[mode]);
  const set = (k: ThemeRole, v: string): void => {
    if (locked) return;
    setDraft({ ...draft, [mode]: { ...draft[mode], [k]: v } });
  };

  const save = async (): Promise<void> => {
    setRefusal(null);
    // REFUSED HERE BEFORE IT IS SENT, in the hand-off's words, and refused
    // again by the service with the same rule if this ever lets one through.
    // Save is already disabled while this mode fails; the other mode is the
    // one somebody cannot see, which is why it is the sentence.
    const other = failingPairs(draft[mode === "light" ? "dark" : "light"]);
    if (other.length > 0) {
      setRefusal({
        kind: "local",
        detail: `Refused: ${other.length} pair${other.length === 1 ? "" : "s"} in the other mode ${
          other.length === 1 ? "is" : "are"
        } below the floor. Both modes ship.`,
      });
      return;
    }
    const problems = themeSaveProblems(draft);
    if (problems.length > 0) {
      setRefusal({ kind: "local", detail: `Refused: ${problems.join("; ")}.` });
      return;
    }
    if (draft.name.trim() === "") {
      setRefusal({ kind: "local", detail: "Refused: a theme needs a name." });
      return;
    }
    setBusy(true);
    const stored = await updateLibraryTheme(api, token, { ...draft, name: draft.name.trim(), themeId: theme.themeId });
    setBusy(false);
    if (!stored.ok) {
      if (stored.kind === "session_ended") onSessionEnded(stored.detail);
      setRefusal(stored);
      return;
    }
    const next = draftOf(stored.value);
    setSaved(next);
    setDraft(next);
    onLibrary({ kind: "put", theme: stored.value });
    say(`Saved. Every surface wearing ${next.name} reads this on its next load.`);
  };

  const duplicate = async (): Promise<void> => {
    setBusy(true);
    setRefusal(null);
    const forClient = show?.clientId ?? clientId ?? null;
    const made = await createLibraryTheme(api, token, { ...copyOf(theme), clientId: forClient });
    setBusy(false);
    if (!made.ok) {
      if (made.kind === "session_ended") onSessionEnded(made.detail);
      setRefusal(made);
      return;
    }
    onLibrary({ kind: "put", theme: made.value });
    say(`Copied. ${made.value.name} is a new theme and is not worn by anything yet.`);
    onOpen(made.value.themeId);
  };

  const archive = async (): Promise<void> => {
    setBusy(true);
    setRefusal(null);
    const gone = await archiveLibraryTheme(api, token, theme.themeId);
    setBusy(false);
    setAsk(false);
    if (!gone.ok) {
      if (gone.kind === "session_ended") onSessionEnded(gone.detail);
      setRefusal(gone);
      return;
    }
    const { clientDefaults, showThemes, surfacePointers } = gone.value.cleared;
    const fell = clientDefaults + showThemes + surfacePointers;
    // Read again rather than patched: the archive cleared pointers on this
    // show, and perhaps on others, that this page has no way to work out.
    onLibrary({ kind: "reload" });
    say(
      fell === 0
        ? `${theme.name} deleted. Nothing was wearing it.`
        : `${theme.name} deleted. ${fell} pointer${fell === 1 ? "" : "s"} fell back to what they follow.`,
    );
    onBack();
  };

  const flip = async (surface: (typeof WEARABLE_SURFACES)[number][0], on: boolean): Promise<void> => {
    if (show === null) return;
    setBusy(true);
    setRefusal(null);
    const next = await saveShowThemes(api, token, {
      activationId: show.activationId,
      showThemeId: show.showThemeId,
      surfaceThemes: flipWear(show, surface, theme.themeId, on),
    });
    setBusy(false);
    if (!next.ok) {
      if (next.kind === "session_ended") onSessionEnded(next.detail);
      setRefusal(next);
      return;
    }
    onShow(next.value);
  };

  return (
    <div className="stack">
      {crumbs}
      <header className="head">
        <h1>{draft.name.trim() === "" ? "Untitled theme" : draft.name}</h1>
        <p className="lede">
          {worn.length > 0
            ? `Worn by ${listed(worn)}. An edit here reaches all of them.`
            : show === null
              ? "Opened from the client's page. A show's Themes page says which of its apps wear it."
              : "Not worn by any surface yet. Point one at it from Themes or from that surface’s own Theme tab."}
        </p>
      </header>

      {theme.locked ? (
        <NoticeBox kind="amber" title="This is a shipped theme">
          <p>
            Nova house ships with the product and is read-only. Copy it to edit a version for{" "}
            {owner ?? "this client"}.
          </p>
          <div className="acts acts-flush">
            <button type="button" className="btn" disabled={busy} onClick={() => void duplicate()}>
              Copy to edit
            </button>
          </div>
        </NoticeBox>
      ) : archived ? (
        <NoticeBox kind="quiet" title="This theme is archived">
          <p>
            Its row is kept as the record of what a published show wore, so it is not edited. Duplicate it to wear
            these colours again.
          </p>
          <div className="acts acts-flush">
            <button type="button" className="btn" disabled={busy} onClick={() => void duplicate()}>
              Duplicate it
            </button>
          </div>
        </NoticeBox>
      ) : null}

      {refusal === null ? null : <ThemeRefused refusal={refusal} />}

      <section>
        <SecLabel>Name</SecLabel>
        <div className="panel panel-stack">
          <div className="pair-wide">
            <FieldRow label="Theme name">
              <input
                className="input"
                name="theme-name"
                value={draft.name}
                disabled={locked}
                maxLength={60}
                onChange={(event) => setDraft({ ...draft, name: event.target.value })}
              />
            </FieldRow>
            {/* READ-ONLY, WHERE THE HAND-OFF HAS A TEXT BOX. A theme's client is
                fixed when it is made: the service refuses to move one, because
                every pointer saved against it would become a pointer at another
                client's palette. So the box shows the owner and does not take
                typing. */}
            <FieldRow
              label="Client"
              help="Who this palette belongs to. Fixed when the theme is made, and shown nowhere a visitor can see."
            >
              <input
                className="input"
                name="theme-client"
                value={theme.clientId === null ? "Nova · any client may wear it" : (owner ?? "This show's client")}
                readOnly
                disabled
              />
            </FieldRow>
          </div>
        </div>
      </section>

      <section>
        <SecLabel>What it looks like</SecLabel>
        <ThemePreviews t={draft} />
      </section>

      <section>
        <SecLabel info="Twelve roles, edited once. A surface that needs a fourteenth colour derives it — the washes behind a confirmation, the soft hairline inside a field — so there is nothing here that only one surface uses.">
          Colour
        </SecLabel>
        <div className="filters theme-mode">
          <ModeSwitch mode={mode} onMode={setMode} />
        </div>
        <PaletteEditor p={draft[mode]} onSet={set} locked={locked} mode={mode} />
      </section>

      <section>
        <SecLabel info="Measured on the palette, so the answer is the same whichever surface wears it. Text needs 4.5:1, the action needs 3:1 to read as a shape, and a hairline is not text.">
          Whether it can be read
        </SecLabel>
        <div className="panel">
          <ContrastTable p={draft[mode]} />
        </div>
      </section>

      <section>
        <SecLabel info="The type scale, the 56px minimum target and the focus ring are not on this page. They are not for sale.">
          Type and corners
        </SecLabel>
        <TypeAndCorners t={draft} locked={locked} onChange={(next) => setDraft({ ...draft, ...next })} />
      </section>

      <section>
        <SecLabel>Where it is worn</SecLabel>
        <div className="panel panel-stack">
          {show === null ? (
            <p className="theme-flush">
              There is no show on this page to point at it. Open a show&rsquo;s Themes page to choose which of its
              apps wear it.
            </p>
          ) : locked ? (
            <p className="theme-flush">
              {theme.locked
                ? "Nothing can be pointed at Nova house. Copy it to edit, and point the apps at the copy."
                : "An archived theme cannot be worn again. Duplicate it and point the apps at the copy."}
            </p>
          ) : (
            WEARABLE_SURFACES.map(([sid, label, , note]) => {
              const on = wears(show, sid, theme.themeId);
              const now = show.surfaces.find((s) => s.surface === sid)?.themeId ?? null;
              return (
                <Setting key={sid} label={label} info={note}>
                  <Switch
                    on={on}
                    label={on ? "Wearing this" : now === null ? "Own system" : `Wearing ${nameOf(themes, now) ?? "another theme"}`}
                    onFlip={() => {
                      if (!busy) void flip(sid, !on);
                    }}
                  />
                </Setting>
              );
            })
          )}
        </div>
      </section>

      <StickyBar
        {...(bad.length > 0
          ? {
              warn: `${bad.length} pair${bad.length === 1 ? "" : "s"} in ${mode} ${
                bad.length === 1 ? "is" : "are"
              } below the floor. Save is refused until they pass.`,
            }
          : {})}
        note={dirty ? "Saved values are what every surface wearing this theme reads on load." : "No unsaved changes."}
      >
        <button type="button" className="btn btn-quiet" disabled={!dirty || busy} onClick={() => setDraft(saved)}>
          Discard
        </button>
        {locked ? null : (
          <button type="button" className="btn btn-danger" disabled={busy} onClick={() => setAsk(true)}>
            Delete
          </button>
        )}
        <button
          type="button"
          className="btn btn-primary"
          disabled={!dirty || locked || bad.length > 0 || busy}
          onClick={() => void save()}
        >
          {busy ? "Saving…" : "Save"}
        </button>
      </StickyBar>

      {ask ? (
        <Confirm
          title={`Delete ${theme.name}?`}
          verb="Delete"
          danger
          {...(worn.length > 0 ? { needType: "DELETE" } : {})}
          onCancel={() => setAsk(false)}
          onConfirm={() => void archive()}
        >
          <p>
            {worn.length > 0
              ? `${listed(worn)} ${worn.length === 1 ? "is" : "are"} wearing this theme${show === null ? "" : " on this show"} and will fall back: an app pointed at it follows the show again, and a show wearing it follows its client's default.`
              : show === null
                ? "Nothing at this client is wearing this theme."
                : "No app on this show is wearing this theme."}
          </p>
          <p>
            Any other show or client default that names it is cleared at the same moment and falls back the same way.
            The theme is archived rather than erased, as the record of what was published with it.
          </p>
          <p>A surface already published keeps the colours it was published with until it is published again.</p>
        </Confirm>
      ) : null}
    </div>
  );
}
